// ─────────────────────────────────────────────────────────────────────────────
// Shared input validation rules for every create/edit form in the customer
// portal.
//
// Kept in sync by hand with the admin portal's copy (botriq-admin-fe ▸
// src/utils/validators.js). The two apps are separate frontends with no shared
// package, so the module can't be imported across — if you change a rule here,
// make the same change there, or the same company name will be accepted by one
// portal and rejected by the other.
//
// The problem this solves: each modal hand-rolled its own checks, and they were
// all presence-only (`if (!name.trim())`). That let plainly illogical values
// through to the database — a robot called ".", a venue called "1", a company
// named "aaaaaaaa", an email of "a@b", a website of "not a url". Presence is not
// the same as plausibility, so the rules below check SHAPE, not just emptiness.
//
// Usage — per field:
//   const err = validateField(RULES.name, form.name);   // string | null
//
// Usage — whole form (returns { field: message } for the fields that failed):
//   const errors = validateForm({ name: RULES.name, email: RULES.email }, form);
//   if (Object.keys(errors).length) { setErrors(errors); return; }
//
// Every rule is a plain object so a form can spread-and-tweak one without
// forking the whole module:
//   { ...RULES.name, label: "Venue name", max: 60 }
// ─────────────────────────────────────────────────────────────────────────────

// ── Primitive character classes ──────────────────────────────────────────────
// A "real" name has to contain at least one letter or digit. This single check
// rejects the entire family of junk that presence-only validation waved through:
// ".", "-", "___", "!!!", "   .  ".
// Unicode-aware on purpose: an ASCII-only class would reject "北京酒店" and
// "Ελλάδα", which are perfectly valid venue names.
const HAS_ALNUM = /[\p{L}\p{N}]/u;

// Letters (incl. accented), digits, space and a small set of punctuation that
// legitimately appears in names: & ' , . - _ / ( ) #
// Deliberately excludes < > { } | \ " ; which are markup/injection shaped and
// have no business in a venue or robot name.
const NAME_ALLOWED = /^[\p{L}\p{N} &'’,.\-_/()#+]+$/u;

// Human names (contact/person): letters, space, apostrophe, hyphen, period only.
const PERSON_NAME_ALLOWED = /^[\p{L} '’.-]+$/u;

// Identifier-ish codes (serial numbers, usernames): no spaces, no punctuation
// beyond - _ . which are the conventional separators.
const CODE_ALLOWED = /^[a-zA-Z0-9._-]+$/;
const USERNAME_ALLOWED = /^[a-zA-Z0-9._-]+$/;

// Linear-time email shape check. Each quantified class excludes the delimiter
// that follows it, so a failing input has no ambiguous split to backtrack over
// (no catastrophic backtracking). Mirrors the customer portal's EMAIL_RE.
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(?:\.[^\s@.]+)+$/; //NOSONAR

// E.164-ish phone: optional +, then 7–15 digits. Separators are stripped before
// this runs, so "+1 (555) 010-9999" is accepted and stored digits-only-ish.
const PHONE_RE = /^\+?\d{7,15}$/;
const PHONE_SEPARATORS = /[\s()\-.]/g;

// MAC address, colon- or hyphen-separated (00:1A:2B:3C:4D:5E). Separators must
// be consistent, which the backreference enforces.
const MAC_RE = /^[0-9a-f]{2}([:-])(?:[0-9a-f]{2}\1){4}[0-9a-f]{2}$/i;

// http/https URL with a dotted host. Rejects "localhost", "not a url", "ftp://…".
const URL_RE = /^https?:\/\/[^\s/?#.]+(?:\.[^\s/?#.]+)+(?:[/?#]\S*)?$/i;

// RFC 5321 caps an address at 254 chars.
const MAX_EMAIL_LENGTH = 254;

// ── Junk-name heuristics ─────────────────────────────────────────────────────
// A name made of one character repeated ("aaaa", "1111", "....") is never a
// deliberate value. Checked on the letters/digits only so "A-1-1-1" survives.
const isRepeatedChar = (s) => {
  const core = s.replace(/[^a-zA-Z0-9]/g, "");
  return core.length >= 3 && new Set(core.toLowerCase()).size === 1;
};

// Sequential keyboard mash. Covers the handful admins actually type when
// testing: asdf / qwerty / 1234 runs of 4+.
const KEYBOARD_RUNS = [
  "qwertyuiop",
  "asdfghjkl",
  "zxcvbnm",
  "0123456789",
  "abcdefghijklmnopqrstuvwxyz",
];
const isKeyboardMash = (s) => {
  const core = s.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
  if (core.length < 4) return false;
  return KEYBOARD_RUNS.some(
    (run) => run.includes(core) || [...run].reverse().join("").includes(core)
  );
};

// Placeholder words that mean "I did not fill this in".
const PLACEHOLDER_WORDS = new Set([
  "test",
  "testing",
  "tests",
  "asdf",
  "asdfasdf",
  "qwerty",
  "dummy",
  "sample",
  "example",
  "foo",
  "bar",
  "baz",
  "temp",
  "tmp",
  "todo",
  "tbd",
  "na",
  "n/a",
  "none",
  "null",
  "undefined",
  "xxx",
  "xxxx",
  "abc",
  "abcd",
  "aaa",
  "string",
  "new",
  "untitled",
  "no name",
  "noname",
  "delete",
  "deleteme",
  "ignore",
]);
const isPlaceholder = (s) => PLACEHOLDER_WORDS.has(s.trim().toLowerCase().replace(/\s+/g, " "));

// ── The single field checker ─────────────────────────────────────────────────
// `rule` shape (all optional except what you need):
//   label       – human name used in messages ("Robot name")
//   required    – reject empty
//   min / max   – trimmed length bounds
//   kind        – "name" | "person" | "code" | "username" | "email" | "phone"
//                 | "url" | "mac" | "number" | "text"
//   min/maxValue– numeric bounds (kind: "number")
//   integer     – kind "number" must be a whole number
//   pattern     – extra RegExp the value must match
//   patternMsg  – message when `pattern` fails
//   oneOf       – array of allowed values (selects)
//   allowJunk   – skip the placeholder/repeat/mash heuristics (rare; e.g. a
//                 serial number legitimately reading "AAA-111")
//
// Returns an error string, or null when the value is acceptable.
export const validateField = (rule, rawValue) => {
  if (!rule) return null;
  const label = rule.label || "This field";
  const value = typeof rawValue === "string" ? rawValue.trim() : rawValue;

  // ── Presence ──
  const isEmpty = value === undefined || value === null || value === "";
  if (isEmpty) return rule.required ? `${label} is required` : null;

  // Non-strings past this point are only meaningfully checked by oneOf/number.
  if (rule.oneOf && !rule.oneOf.includes(rawValue)) {
    return `Select a valid ${label.toLowerCase()}`;
  }

  if (rule.kind === "number") return validateNumber(rule, value, label);

  const str = String(value);

  // ── Length, shape, caller pattern, plausibility ──
  // Each step returns a message or null; the first message wins.
  return (
    validateLength(rule, str, label) ||
    validateShape(rule, str, label) ||
    validatePattern(rule, str, label) ||
    validatePlausibility(rule, str, label)
  );
};

const validateLength = (rule, str, label) => {
  if (rule.min && str.length < rule.min) {
    return `${label} must be at least ${rule.min} characters`;
  }
  if (rule.max && str.length > rule.max) {
    return `${label} must be ${rule.max} characters or fewer`;
  }
  return null;
};

const validatePattern = (rule, str, label) => {
  if (rule.pattern && !rule.pattern.test(str)) {
    return rule.patternMsg || `${label} is not valid`;
  }
  return null;
};

// The junk heuristics only make sense for human-authored labels. Machine-shaped
// values (an email, a URL, a phone number) are already fully constrained by
// their own format check, and "aaaa@aaaa.com" is a real address.
const FORMATTED_KINDS = new Set(["email", "url", "phone", "mac"]);

const validatePlausibility = (rule, str, label) => {
  if (rule.allowJunk || FORMATTED_KINDS.has(rule.kind)) return null;
  if (isPlaceholder(str)) return `${label} looks like placeholder text`;
  if (isRepeatedChar(str)) return `${label} cannot be the same character repeated`;
  if (isKeyboardMash(str)) return `Enter a real ${label.toLowerCase()}`;
  return null;
};

// Numeric branch, split out to keep validateField's cognitive complexity low
// (javascript:S3776).
const validateNumber = (rule, value, label) => {
  const num = Number(value);
  if (Number.isNaN(num)) return `${label} must be a number`;
  if (rule.integer && !Number.isInteger(num)) return `${label} must be a whole number`;
  if (rule.minValue !== undefined && num < rule.minValue) {
    return `${label} must be at least ${rule.minValue}`;
  }
  if (rule.maxValue !== undefined && num > rule.maxValue) {
    return `${label} must be at most ${rule.maxValue}`;
  }
  return null;
};

// Per-kind shape rules. A lookup of small checkers rather than a switch, so
// adding a `kind` is a one-line entry and the dispatch stays flat.
const SHAPE_CHECKS = {
  email: (str) =>
    str.length > MAX_EMAIL_LENGTH || !EMAIL_RE.test(str) ? "Enter a valid email address" : null,

  phone: (str) =>
    PHONE_RE.test(str.replace(PHONE_SEPARATORS, ""))
      ? null
      : "Enter a valid phone number (10 digits)",

  url: (str, label) =>
    URL_RE.test(str) ? null : `${label} must be a full URL (https://example.com)`,

  mac: (str, label) => (MAC_RE.test(str) ? null : `${label} must look like 00:1A:2B:3C:4D:5E`),

  person: (str, label) => {
    if (!HAS_ALNUM.test(str)) return `${label} must contain letters`;
    if (!PERSON_NAME_ALLOWED.test(str)) return `${label} can only contain letters, spaces, ' and -`;
    return null;
  },

  username: (str) => {
    if (!USERNAME_ALLOWED.test(str)) return "Username can only contain letters, numbers, . _ and -";
    if (!HAS_ALNUM.test(str)) return "Username must contain letters or numbers";
    return null;
  },

  code: (str, label) => {
    if (!CODE_ALLOWED.test(str)) return `${label} can only contain letters, numbers, . _ and -`;
    if (!HAS_ALNUM.test(str)) return `${label} must contain letters or numbers`;
    return null;
  },

  name: (str, label) => {
    if (!HAS_ALNUM.test(str)) return `${label} must contain letters or numbers`;
    if (!NAME_ALLOWED.test(str)) return `${label} contains invalid characters`;
    return null;
  },
};

const validateShape = (rule, str, label) => {
  const check = SHAPE_CHECKS[rule.kind];
  return check ? check(str, label) : null;
};

// ── Whole-form checker ───────────────────────────────────────────────────────
// `rules` is { fieldName: rule }; `values` is the form state. Returns a
// { fieldName: message } map containing ONLY the fields that failed, so
// `Object.keys(errors).length === 0` means the form is good.
export const validateForm = (rules, values) => {
  const errors = {};
  for (const [field, rule] of Object.entries(rules)) {
    const message = validateField(rule, values?.[field]);
    if (message) errors[field] = message;
  }
  return errors;
};

// Convenience for confirm-password style pairs, which no single-field rule can
// express. Returns a message or null.
export const matchError = (a, b, label = "Passwords") => (a !== b ? `${label} do not match` : null);

// Duplicate guard for inline list editors (venue floors, property hotels).
// Case- and whitespace-insensitive. Returns a message or null.
export const duplicateError = (names, label = "name") => {
  const seen = new Set();
  for (const n of names) {
    const key = String(n).trim().toLowerCase().replace(/\s+/g, " ");
    if (seen.has(key)) return `Duplicate ${label}: "${String(n).trim()}"`;
    seen.add(key);
  }
  return null;
};

// ── Ready-made rules for the fields that recur across the portal ─────────────
// Forms should prefer these over re-declaring shapes, so "what counts as a valid
// venue name" has exactly one definition.
export const RULES = {
  // Entity names — company, venue, robot, map, property, hotel, floor.
  entityName: { label: "Name", required: true, kind: "name", min: 2, max: 100 },
  // Serial numbers / asset tags. allowJunk because "SN-AAA-111" is legitimate.
  serialNumber: {
    label: "Serial number",
    required: true,
    kind: "code",
    min: 3,
    max: 60,
    allowJunk: true,
  },
  macAddress: { label: "MAC address", required: true, kind: "mac" },
  person: { label: "Name", required: true, kind: "person", min: 2, max: 80 },
  username: { label: "Username", required: true, kind: "username", min: 3, max: 40 },
  email: { label: "Email", required: true, kind: "email" },
  phone: { label: "Phone", required: true, kind: "phone" },
  website: { label: "Website", kind: "url", max: 200 },
  address: { label: "Address", kind: "name", min: 4, max: 200 },
  city: { label: "City", kind: "person", min: 2, max: 60 },
  state: { label: "State", kind: "person", min: 2, max: 60 },
  country: { label: "Country", kind: "person", min: 2, max: 60 },
  description: { label: "Description", kind: "text", max: 500 },
};

export default validateField;
